import React from 'react';

export type OperationState = 'pending' | 'success' | 'error';

export interface OperationStatusProps {
  state: OperationState;
  label: string;
  description?: string;
  expectedDuration?: string;
  onCancel?: () => void;
  onRetry?: () => void;
}

const stateClasses: Record<OperationState, string> = {
  pending: 'border-primary-200 bg-primary-50 text-primary-900 dark:border-primary-800 dark:bg-primary-900 dark:text-primary-100',
  success: 'border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950 dark:text-green-100',
  error: 'border-danger-200 bg-danger-50 text-danger-900 dark:border-danger-800 dark:bg-danger-900 dark:text-danger-100',
};

const OperationStatus: React.FC<OperationStatusProps> = ({
  state,
  label,
  description,
  expectedDuration,
  onCancel,
  onRetry,
}) => (
  <div
    role={state === 'error' ? 'alert' : 'status'}
    aria-label={label}
    aria-live={state === 'error' ? 'assertive' : 'polite'}
    className={`rounded-lg border p-4 ${stateClasses[state]}`}
  >
    <div className="flex items-start gap-3">
      {state === 'pending' && (
        <svg
          className="mt-0.5 h-5 w-5 shrink-0 animate-spin motion-reduce:animate-none"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-medium">{label}</div>
        {description && <div className="mt-1 text-sm opacity-80">{description}</div>}
        {expectedDuration && <div className="mt-1 text-sm opacity-80">{expectedDuration}</div>}
      </div>
      {state === 'pending' && onCancel && (
        <button type="button" onClick={onCancel} className="text-sm font-medium underline underline-offset-2">
          Cancel request
        </button>
      )}
      {state === 'error' && onRetry && (
        <button type="button" onClick={onRetry} className="text-sm font-medium underline underline-offset-2">
          Retry
        </button>
      )}
    </div>
  </div>
);

export default OperationStatus;
